import type { Dispatch } from 'redux';
import {
    createActionFactoryForFeature,
    Feature,
    UnifiedGroup,
    createActionFactory,
    createThunk,
    ThunkDispatch,
    groupHubOwnerFolderUrlKey,
    groupHubUrlKey,
} from '../../../../shared';
import type {
    UnifiedGroupFilter,
    UnifiedGroupSorting,
} from '../../../models/unifiedGroupListViewState';
import { getUserUnifiedGroupsData } from '../../data/groups/unifiedGroupActions';
import { getPackageState } from '../../../../appstate';
import type { HubState } from '../../../selectors';
import {
    packageStateKey,
    getGroupedUnifiedGroupListEntries,
    UnifiedGroupListEntry,
} from '../../..';
import navigateToRoute from 'owa-router/lib/navigateToRoute';
import { isFeatureEnabled } from 'owa-feature-flags';
import { isConsumer } from 'owa-session-store';
import { lazyLogFloodgateActivity } from 'owa-floodgate-feedback-view';
import { isHostAppFeatureEnabled } from 'owa-hostapp-feature-flags';

export type { UnifiedGroup };

const actionFactory = createActionFactoryForFeature(Feature.UnifiedGroups);
const floodgateSurveyDelay = 1000;
export const unifiedGroupListFilterChangedAction = createActionFactory<{
    filter: UnifiedGroupFilter;
}>('GroupsFilterChangedAction', Feature.UnifiedGroups, action => ({
    filter: action.payload.filter,
}));

export const unifiedGroupSortingChangedAction = createActionFactory<{
    sorting: UnifiedGroupSorting;
}>('GroupsSortingChangedAction', Feature.UnifiedGroups, action => ({
    sorting: action.payload.sorting,
}));

export const unifiedGroupListScrollToGroupClickedAction = actionFactory<{
    firstHubGroupIdInGroup: string;
}>('GroupsListScrollToGroupClickedAction');

export type UnifiedGroupClickedOrigin = 'unifiedGroupList';

export const unifiedGroupListRetryAction = actionFactory<{}>('GroupsListRetryAction');

export const unifiedGroupClickedAction = actionFactory<{
    unifiedGroupId: string;
    origin: UnifiedGroupClickedOrigin;
}>('UnifiedGroupClickedAction');

export const unifiedGroupFocusAction = actionFactory<{
    unifiedGroupId: string;
    unifiedGroupSmtp: string;
}>('UnifiedGroupFocusAction');

export const unifiedGroupTransferOwnershipAction = actionFactory<{
    unifiedGroupId: string;
    origin: UnifiedGroupClickedOrigin;
    isTransferOwnershipDialog: boolean;
}>('UnifiedGroupTransferOwnershipAction');

export const unifiedGroupViewFilterChanged = (dispatch: ThunkDispatch) => (
    filter: UnifiedGroupFilter
) => {
    dispatch(unifiedGroupListFilterChangedAction({ filter }));
    dispatch(changeRouteForFilterChange());
};

export const unifiedGroupViewSortingChanged = (dispatch: Dispatch) => (
    sorting: UnifiedGroupSorting
) => dispatch(unifiedGroupSortingChangedAction({ sorting }));

export const unifiedGroupListScrollToGroupClicked = (dispatch: Dispatch) => (
    firstHubGroupIdInGroup: string
) => dispatch(unifiedGroupListScrollToGroupClickedAction({ firstHubGroupIdInGroup }));

export const unifiedGroupListItemClicked = (dispatch: Dispatch) => (
    unifiedGroup: UnifiedGroup,
    origin: UnifiedGroupClickedOrigin
) => {
    dispatch(
        unifiedGroupClickedAction({
            unifiedGroupId: unifiedGroup.hubPersonaId,
            origin,
        })
    );
};

export const unifiedGroupTransferOwnershipClicked = (dispatch: Dispatch) => (
    unifiedGroup: UnifiedGroup,
    origin: UnifiedGroupClickedOrigin,
    isTransferOwnershipDialog: boolean
) => {
    dispatch(
        unifiedGroupTransferOwnershipAction({
            unifiedGroupId: unifiedGroup.hubPersonaId,
            origin,
            isTransferOwnershipDialog,
        })
    );
};

export const unifiedGroupListItemFocus = (dispatch: Dispatch) => (unifiedGroup: UnifiedGroup) => {
    dispatch(
        unifiedGroupFocusAction({
            unifiedGroupId: unifiedGroup.hubPersonaId,
            unifiedGroupSmtp: unifiedGroup.emailAddress,
        })
    );
    if (
        isFeatureEnabled('tri-floodgateForConsumerGroups') &&
        isHostAppFeatureEnabled('floodgate') &&
        isConsumer()
    ) {
        setTimeout(function () {
            lazyLogFloodgateActivity.import().then(logFloodgateActivity => {
                logFloodgateActivity('ConsumerGroupsClicked');
            });
        }, floodgateSurveyDelay);
    }
};

export const unifiedGroupListRetryActionClicked = (dispatch: Dispatch): void => {
    dispatch(unifiedGroupListRetryAction());
    getUserUnifiedGroupsData(dispatch);
};

const changeRouteForFilterChange = () =>
    createThunk(
        'ChangeRouteForFilterChange',
        (dispatch: ThunkDispatch, getState: () => {}): void => {
            const packageState = getPackageState<HubState>(getState(), packageStateKey);
            let groups: UnifiedGroupListEntry[];
            if (packageState) {
                groups = getGroupedUnifiedGroupListEntries(
                    packageState.appState,
                    packageState.packageState
                );
                // change the url to people/group/owner when there are no groups after filter as filter is only in owner section
                if (groups.length == 0) {
                    navigateToRoute([groupHubUrlKey, groupHubOwnerFolderUrlKey]);
                }
            }
        }
    );
export type { Action } from '../../../../shared';
